import React, { memo, useMemo } from 'react';
import { Draggable, DraggableProvided } from 'react-beautiful-dnd';
import Section from './Section';
import {
  BoardTitle,
  Container,
  DoneCheckbox,
  AddNewTaskIcon,
  EditableTitle,
  TitleText,
  Loader,
  SectionMenu,
  InfoTooltip,
  InfoTooltipWrapper,
  UnassignedTitleText,
} from './styles';
import { ColumnProps, TaskCreationPosition } from './types';
import useNotebookBoardTasksController from './useNotebookBoardTasksController';
import ThemeV2 from '../../../theme';
import SVGIcon from '../../../atomic/atoms/SVGIcon';
import { Flex } from '../../../Utils/styles/display';
import { NO_SECTION_TOOLTIP_LABEL } from '../../../languages/en/notebook';
import {
  completedSectionColorConfig,
  defaultColorConfig,
} from './useNotebookBoardTasksController/data';
import { DummySection } from './DummySection';

const NotebookBoardTasksController = (props: ColumnProps) => {
  const {
    sectionId,
    index,
    toggleModal,
    isLoading,
    newSectionCreationData,
    setNewSectionCreationData,
    dummySectionTitle,
    setDummySectionTitle,
    handleSectionCreation,
    isDummySectionLoading = false,
    markTasksAsRead,
  } = props;
  const {
    section,
    isCreateTaskMode,
    taskCreationPosition,
    sectionDropdownOptions,
    handleTaskCreationButtonClick,
    handleTaskCreationTextBlur,
    handleTaskCreationEnterClick,
    handleSectionTitleChange,
    handleSectionTitleFocus,
    isInEditMode,
    sectionTitle,
    setEditMode,
    isSectionBeingEdited,
    handleSectionMenuItemClick,
    completedSectionOptions,
    handleCompletedFilterSelect,
    notesLength,
    onTaskDataChange,
  } = useNotebookBoardTasksController(
    sectionId,
    toggleModal,
    setNewSectionCreationData,
  );

  const { name, isCompleted, isUnassigned } = section;

  const customColorConfig = useMemo(
    () => (isCompleted ? completedSectionColorConfig : defaultColorConfig),
    [isCompleted],
  );

  const renderInput = useMemo(() => {
    if (isSectionBeingEdited || isLoading) {
      return (
        <>
          <EditableTitle
            color={ThemeV2.palette.gray1}
            value={sectionTitle}
            onChange={() => {}}
            disabled
          />
          <Loader icon="loader" size="16px" />
        </>
      );
    }
    if (isInEditMode)
      return (
        <EditableTitle
          value={sectionTitle}
          onChange={handleSectionTitleChange}
          onBlur={handleSectionTitleFocus}
          onKeyPress={(e: React.KeyboardEvent) => {
            if (e.key === 'Enter') {
              handleSectionTitleFocus();
            }
            // TODO: Sumedha - Debug why space does not seem to work for this instance of the component
            if (e.key === ' ') {
              return;
            }
          }}
          autoFocus
        />
      );
    return (
      <InfoTooltipWrapper
        onClick={() => {
          setEditMode(true);
        }}
      >
        <InfoTooltip
          toolTipComponent={<TitleText variant="body1Medium">{name}</TitleText>}
          className="section-title-tooltip"
          data-testid="section-title-tooltip"
          data-qa-id="tooltip-section-title"
        >
          {name}
        </InfoTooltip>
      </InfoTooltipWrapper>
    );
  }, [
    isSectionBeingEdited,
    isLoading,
    isInEditMode,
    sectionTitle,
    handleSectionTitleChange,
    handleSectionTitleFocus,
    name,
    setEditMode,
  ]);

  if (isUnassigned && notesLength === 0) return null;

  // Note: isDragDisabled should be false for custom sections, hardcoded to true for now
  return (
    <>
      {newSectionCreationData?.currentSectionId === sectionId &&
        newSectionCreationData?.position === 'left' && (
          <DummySection
            isSectionBeingCreated={isDummySectionLoading}
            handleSectionCreation={handleSectionCreation}
            autoFocus={newSectionCreationData?.currentSectionId === sectionId}
            sectionTitle={dummySectionTitle}
            setSectionTitle={setDummySectionTitle}
          />
        )}

      <Draggable
        draggableId={sectionId}
        isDragDisabled={isUnassigned}
        index={index}
      >
        {(provided: DraggableProvided) => (
          <Container ref={provided.innerRef} {...provided.draggableProps}>
            <BoardTitle
              {...provided.dragHandleProps}
              justifyContent="space-between"
              aria-label={`${sectionId}`}
              background={isCompleted ? ThemeV2.palette.polarGreen2 : undefined}
              hasAddButton={!isCompleted}
            >
              <Flex inlineFlex flexGrow={1}>
                {isCompleted && (
                  <DoneCheckbox
                    name={`note-create-circular-checkbox`}
                    onChange={() => {}}
                    size="20px"
                    value
                  />
                )}
                {isUnassigned ? (
                  <InfoTooltip
                    toolTipComponent={
                      <Flex inlineFlex flexGrow={1}>
                        <UnassignedTitleText variant="body1Medium">
                          {name}
                        </UnassignedTitleText>
                        <SVGIcon
                          icon="info-icon"
                          color={ThemeV2.palette.gray9}
                          size="16px"
                        />
                      </Flex>
                    }
                    data-testid="section-title-tooltip"
                    className="section-title-tooltip"
                    data-qa-id="tooltip-section-title"
                  >
                    {NO_SECTION_TOOLTIP_LABEL}
                  </InfoTooltip>
                ) : (
                  renderInput
                )}
              </Flex>
              {!(isCompleted || isUnassigned) && (
                <AddNewTaskIcon
                  tabIndex={0}
                  role="button"
                  onClick={() =>
                    handleTaskCreationButtonClick(TaskCreationPosition.top)
                  }
                >
                  <SVGIcon
                    icon="union"
                    color={ThemeV2.palette.gray8}
                    size="24px"
                  />
                </AddNewTaskIcon>
              )}
              {isCompleted && (
                <SectionMenu
                  menuItems={completedSectionOptions || []}
                  icon="filter"
                  title="filter"
                  onItemClick={handleCompletedFilterSelect}
                  iconColor={ThemeV2.palette.gray8}
                  stopEventPropagation
                  variant="custom"
                  customColorConfig={customColorConfig}
                />
              )}
              {!isUnassigned && (
                <SectionMenu
                  menuItems={sectionDropdownOptions || []}
                  icon="more"
                  title="more"
                  onItemClick={handleSectionMenuItemClick}
                  iconColor={ThemeV2.palette.gray8}
                  stopEventPropagation
                  variant="custom"
                  customColorConfig={customColorConfig}
                />
              )}
            </BoardTitle>
            <Section
              section={section}
              isCreateTaskMode={isCreateTaskMode}
              taskCreationPosition={taskCreationPosition}
              handleTaskCreationTextBlur={handleTaskCreationTextBlur}
              handleTaskCreationEnterClick={handleTaskCreationEnterClick}
              handleTaskCreationButtonClick={handleTaskCreationButtonClick}
              isLoading={isLoading}
              markTasksAsRead={markTasksAsRead}
              onTaskDataChange={onTaskDataChange}
            />
          </Container>
        )}
      </Draggable>

      {newSectionCreationData?.currentSectionId === sectionId &&
        newSectionCreationData?.position === 'right' && (
          <DummySection
            isSectionBeingCreated={isDummySectionLoading}
            handleSectionCreation={handleSectionCreation}
            autoFocus={newSectionCreationData?.currentSectionId === sectionId}
            sectionTitle={dummySectionTitle}
            setSectionTitle={setDummySectionTitle}
          />
        )}
    </>
  );
};

export default memo(NotebookBoardTasksController);
